import { onMounted, ref } from 'vue';
import axios from 'axios';
import { GlobalEventAction, ACCESS_TOKEN_KEY } from '../constant/enums';
import emitter from '@/mitt';

let nonceFromUrl;

// Module-scoped so the Axios request interceptor in `http.ts` can read it
// without importing the composable's return value (which is component-bound).
export const isAuthenticated = ref(false);

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for non-secure HTTP contexts (e.g. local dev over HTTP)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const BASE_URL = ((process.env as any).VUE_APP_APM_BROKER_URL as string);

const trustedOrigins: string[] = (() => {
  const raw = (process.env as any).VUE_APP_TRUSTED_ORIGINS as string;  
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) 
      return parsed;
  } catch (e) {
    // fall through to fallback
    console.warn('Failed to parse VUE_APP_TRUSTED_ORIGINS as JSON array, falling back to comma-separated list if applicable.');
  }
  // If the env was provided as a comma-separated list, support that too
  if (raw.includes(',')) {
    return raw.split(',').map(s => s.trim()).filter(Boolean);
  }
  return [];
})();

const targetOrigin = trustedOrigins.includes(document.referrer?.split("/").slice(0, 3).join("/"))
  ? document.referrer.split("/").slice(0, 3).join("/")
  : "*";

export function useIframeAuth(mode: string, audience: string = 'platmap') {
  const jwt = ref<string | null>(null);
  const isReauthenticating = ref(false);
  const error = ref<string | null>(null);

  let resolveReady: () => void;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });

  const decodeJwt = (token: string): { exp: number } => {    
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  };

  const setupApiWithToken = (token: string) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.defaults.headers.common['type'] = mode;
  };

  const handleTokenExpiration = (token: string) => {
    const { exp } = decodeJwt(token);
    const delay = exp * 1000 - Date.now() - 30000; // 30 seconds before actual expiration    
    if (delay > 0) {
      setTimeout(() => {
        // Proactive: token still valid, silent — no overlay        
        window.parent.postMessage({ type: 'APM_REAUTH_REQUIRED', nonce: nonceFromUrl, audience: 'platmap', requestId: generateId() }, targetOrigin);
      }, delay);
    }
  };

  const sendReauthMessage = (reason?) => {
      isReauthenticating.value = true;
      window.parent.postMessage({ type: 'APM_REAUTH_REQUIRED', nonce: nonceFromUrl, audience: 'platmap', requestId: generateId(),  reason }, targetOrigin);
  }

  const exchangeCodeForToken = async (code: string, nonce: string) => {
    try {      
      const response = await fetch(`${BASE_URL}/token/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token_exchange: { code, nonce, audience } }),
      });

      if (!response.ok) {
        window.parent.postMessage({ type:'APM_REAUTH_REQUIRED', nonce: nonceFromUrl , audience:'platmap', reason:'exchange_failed' }, targetOrigin);
        throw new Error('Token exchange failed'); 
      }
      else{
        console.log('Platmap: Token exchange successful');
      }
      const responseJson = await response.json();      
      const { access_token } = responseJson.token_exchange;
      console.log('Platmap: Received access token');
      jwt.value = access_token;
      isAuthenticated.value = true;
      localStorage.setItem(ACCESS_TOKEN_KEY, access_token);

      setupApiWithToken(access_token);
      handleTokenExpiration(access_token);
      isReauthenticating.value = false;
      resolveReady?.();
    } catch (err: any) {
      error.value = err.message || 'Unknown error during auth';
      console.error('Iframe auth error:', err);
    }
  };

  const listenForAuthMessage = (expectedNonce: string | null) => {
    let isBootstrapped = false;

    window.addEventListener('message', (event) => {
      console.log('Platmap: Received message from origin', event.origin);
      if (!trustedOrigins.includes(event.origin)) return;

      const { type, code, nonce } = event.data || {};
      if (type !== 'APM_CODE' || !code || !nonce) {
        console.warn('Platmap: APM_ERROR - Invalid message format or missing code/nonce or type !== APM_CODE');
        window.parent.postMessage({ type:'APM_ERROR', requestId: generateId(), error:'missing_code' }, targetOrigin);
        return;
      }
      console.log('Platmap: Received auth code message with nonce.');

      // On first load only, verify the nonce matches the URL query parameter
      if (!isBootstrapped) {
        if (nonce !== expectedNonce) {
          console.warn('Nonce mismatch with URL query parameter');
          return;
        }
        isBootstrapped = true;
      }

      exchangeCodeForToken(code, nonce);
    });
    window.parent.postMessage({ type: "READY" }, targetOrigin);
  };

  onMounted(() => {
    emitter.on(GlobalEventAction.Unauthorized, () => sendReauthMessage('404'));
    const urlParams = new URLSearchParams(window.location.search);
    nonceFromUrl = urlParams.get('nonce');    
    if (!nonceFromUrl) {
      error.value = 'Nonce is missing from URL';      
      window.parent.postMessage({ type:'APM_ERROR', requestId: generateId(), error: 'nonce_mismatch' }, targetOrigin);
      return;
    }

    listenForAuthMessage(nonceFromUrl);
  });

  return {
    jwt,
    isAuthenticated,
    isReauthenticating,
    error,
    ready,
    sendReauthMessage
  };
}
